// ================================================================
//  InterceptorsPanelController
//  Account-wide view of every biz interceptor across every profile —
//  same row shape (toggle, name, transport, environment dots + pressure,
//  owner/last-changed, actions) as BizInterceptorsTabController's own
//  per-profile list, just fed by the same GET /biz-interceptor endpoint
//  with profile_id omitted (see BizInterceptorController#getAllBizInterceptors,
//  BizInterceptorService.listByProfile — a falsy profileId already means
//  "every profile in the account"). Controls/layout mirror
//  AgentsPanelController's own top-level panel (search/sort/page-size/
//  pagination + a silent auto-refresh), rather than the tab's fixed-bar
//  variant, so the two account-wide panels feel like the same experience.
//
//  Every action icon opens the exact same dialog the per-profile tab
//  opens for that interceptor — Consolidated Stats, Metrics Explorer,
//  Download Artifact, the Environment Status dialog (env dots), and the
//  full Edit wizard. Only the Edit wizard needs a full Profile object
//  (profile.environments, profile.profileName) beyond what the list
//  response carries — fetched on demand via ProfileService.getProfileById
//  right before that dialog opens, rather than eagerly for every row.
// ================================================================

angular.module('bizmetryApp').controller('InterceptorsPanelController', function (
    $scope, $mdDialog, $mdToast, $timeout, $interval,
    BizInterceptorService, ProfileService, ConfigurationService, dialogService
) {

    // Keep the account-wide panel consistent with the per-profile summary.
    $scope.getInterceptorTransport = function (ic) {
        return BizInterceptorService.describeTransport((ic && ic.transport_type) || 'REST_API');
    };

    // ─── State ───────────────────────────────────────
    $scope.loading = true;
    $scope.refreshing = false;
    $scope.interceptors = [];
    $scope.profiles = [];
    $scope.profileNameById = {};
    // Per-environment initial-letter header (the "D", "I", ... circles
    // sitting above the Environments column) — only meaningful once a
    // single Profile is selected. With "All" profiles selected, different
    // rows belong to different profiles with different environment sets,
    // so there's no one fixed list of environments a header could point
    // to — the plain "Environments" text label stays in that case (see
    // interceptors-panel.html). Matches the per-profile Biz Interceptors
    // tab's own header (biz-interceptors-tab.html's profile.environments),
    // just fetched on demand here since this panel's row data itself
    // doesn't carry the full Profile object.
    $scope.selectedProfileEnvironments = null;

    // ─── Filters / controls ────────────────────────────
    $scope.selectedProfile = 'ALL';
    $scope.searchText = '';
    // Server-side filter (see BizInterceptorServiceImpl#COMPUTED_SORT_FIELDS'
    // sibling live-filter path) — only interceptors with at least one
    // ONLINE environment come back from the API when this is on; off shows
    // everything, same as today.
    $scope.liveOnly = false;
    // The last four options sort by a value that isn't a persisted column
    // on BizInterceptor (captured/dropped/pressure are only ever computed
    // per-row from each interceptor's own time-series data) — the backend
    // detects these by field name and switches to an in-memory sort-then-
    // paginate path instead of its normal DB-level Pageable sort. See
    // BizInterceptorServiceImpl#COMPUTED_SORT_FIELDS.
    $scope.sortOptions = [
        { value: 'name_asc', label: 'Name (A-Z)', field: 'biz_interceptor_name', dir: 'asc' },
        { value: 'name_desc', label: 'Name (Z-A)', field: 'biz_interceptor_name', dir: 'desc' },
        { value: 'date_asc', label: 'Oldest First', field: 'created_ts', dir: 'asc' },
        { value: 'date_desc', label: 'Newest First', field: 'created_ts', dir: 'desc' },
        { value: 'captured_desc', label: 'Most Captured', field: 'total_frames_captured', dir: 'desc' },
        { value: 'captured_asc', label: 'Least Captured', field: 'total_frames_captured', dir: 'asc' },
        { value: 'dropped_desc', label: 'Most Dropped', field: 'total_frames_dropped', dir: 'desc' },
        { value: 'pressure_desc', label: 'Highest Avg Pressure', field: 'avg_load_pressure', dir: 'desc' }
    ];
    $scope.selectedSort = 'name_asc';

    $scope.paging = {
        page: 0,
        pageSize: ConfigurationService.getFromCache('frontend.profile_page_size') || 10,
        pageSizeOptions: [5, 10, 15, 25, 50, 100],
        totalItems: 0,
        totalPages: 0
    };

    var searchDebounceMs = ConfigurationService.getFromCache('frontend.search_debounce_time') || 1000;
    var refreshIntervalMs = ConfigurationService.getFromCache('frontend.biz_interceptors_refresh_interval') || 10000;

    var searchDebounceTimer = null;
    var refreshTimer = null;
    var isFirstLoad = true;

    // ─── Reference data — profile filter dropdown + id→name lookup for
    // the Profile column (BizInterceptorDTO only carries profile_id, same
    // as TelemetryAgentService rows carry profileName straight from the
    // agent backend — biz interceptors don't, so it's resolved client-side
    // here instead). ────────────────────────────────────
    function _loadFilterData() {
        ProfileService.getAllProfiles()
            .then(function (response) {
                $scope.profiles = (response.data && response.data.profiles) || [];
                var map = {};
                $scope.profiles.forEach(function (p) { map[p.id] = p.profileName; });
                $scope.profileNameById = map;
            })
            .catch(function (err) { console.error('Error loading profiles:', err); });
    }

    $scope.profileName = function (ic) {
        return (ic && $scope.profileNameById[ic.profile_id]) || '—';
    };

    // Fetches the full Profile (for its .environments list) whenever the
    // Profile filter is narrowed to one specific profile — this is what
    // lets the Environments column header show that profile's per-
    // environment initial letters, same as the per-profile Biz Interceptors
    // tab's own header. Cleared back to null for "All" profiles, since no
    // single environment list applies across every row in that case.
    function loadSelectedProfileEnvironments() {
        if ($scope.selectedProfile === 'ALL' || !$scope.selectedProfile) {
            $scope.selectedProfileEnvironments = null;
            return;
        }
        ProfileService.getProfileById($scope.selectedProfile)
            .then(function (response) {
                $scope.selectedProfileEnvironments = (response.data && response.data.environments) || [];
            })
            .catch(function (err) {
                console.error('Error loading profile environments:', err);
                $scope.selectedProfileEnvironments = null;
            });
    }

    // ─── Sort resolution ───────────────────────────────
    function resolveSort() {
        var opt = $scope.sortOptions.find(function (o) { return o.value === $scope.selectedSort; });
        return opt ? { field: opt.field, dir: opt.dir } : { field: 'biz_interceptor_name', dir: 'asc' };
    }

    // ─── Core fetch ────────────────────────────────────
    // `silent`: used by the auto-refresh poll — skips the spinner so rows
    // just swap data in place instead of blanking the list every tick.
    //
    // requestSeq guards against the silent auto-refresh interval (fires
    // independently every refreshIntervalMs) and a debounced search/filter
    // call landing out of order — e.g. a refresh tick issued just before
    // the user typed, still in flight with the OLD searchText, resolving
    // AFTER the search's own (correctly filtered) response already
    // rendered would silently overwrite it with the stale, unfiltered
    // list — the exact "full list flashes back after the filtered one
    // already showed" bug this was written to fix. Only the response whose
    // sequence number still matches the latest call is ever applied.
    var requestSeq = 0;
    var listDestroyed = false;

    $scope.loadInterceptors = function (silent) {
        if (listDestroyed || BizInterceptorService.isListRefreshPaused()) return;
        if (!silent) { $scope.loading = true; } else { $scope.refreshing = true; }

        var sort = resolveSort();
        var profileId = ($scope.selectedProfile === 'ALL' || !$scope.selectedProfile) ? null : $scope.selectedProfile;
        var mySeq = ++requestSeq;

        BizInterceptorService.listByProfile(profileId, {
            pauseWithMetrics: true,
            page: $scope.paging.page,
            size: $scope.paging.pageSize,
            search: $scope.searchText,
            sortField: sort.field,
            sortDir: sort.dir,
            liveOnly: $scope.liveOnly
        })
            .then(function (response) {
                if (mySeq !== requestSeq || BizInterceptorService.isListRefreshPaused()) { return; } // superseded or covered by Metrics Explorer

                var data = response && response.data;
                $scope.interceptors = (data && data.content) || [];
                $scope.paging.totalPages = (data && data.totalPages) || 0;
                $scope.paging.totalItems = (data && data.totalElements) || 0;
                $scope.paging.page = (data && data.page) || 0;

                if ($scope.paging.page >= $scope.paging.totalPages && $scope.paging.totalPages > 0) {
                    $scope.paging.page = $scope.paging.totalPages - 1;
                }
            })
            .catch(function (err) {
                if (err && err.status === -1) return;
                if (mySeq !== requestSeq || BizInterceptorService.isListRefreshPaused()) { return; } // superseded or covered by Metrics Explorer

                if (err && err.status === 204) {
                    $scope.interceptors = [];
                    return;
                }
                console.error('Error loading biz interceptors:', err);
                if (!silent) { $scope.interceptors = []; }
            })
            .finally(function () {
                if (mySeq !== requestSeq) { return; } // superseded by a newer call — discard

                $scope.loading = false;
                $scope.refreshing = false;
                isFirstLoad = false;
            });
    };

    // ─── Filter/sort/page-size controls ────────────────
    $scope.updateFilters = function () {
        if (searchDebounceTimer) { $timeout.cancel(searchDebounceTimer); }
        searchDebounceTimer = $timeout(function () {
            $scope.paging.page = 0;
            $scope.loadInterceptors();
        }, searchDebounceMs);
    };

    // The search box's own "×" — a single deliberate click, not a
    // keystroke — should clear and reload immediately rather than sitting
    // through the same debounce meant to smooth out typing.
    $scope.clearSearch = function () {
        if (searchDebounceTimer) { $timeout.cancel(searchDebounceTimer); }
        $scope.searchText = '';
        $scope.paging.page = 0;
        $scope.loadInterceptors();
    };

    $scope.onSortChange = function () {
        $scope.paging.page = 0;
        $scope.loadInterceptors();
    };

    $scope.onProfileChange = function () {
        $scope.paging.page = 0;
        loadSelectedProfileEnvironments();
        $scope.loadInterceptors();
    };

    $scope.onPageSizeChange = function () {
        $scope.paging.page = 0;
        $scope.loadInterceptors();
    };

    $scope.onLiveOnlyChange = function () {
        $scope.paging.page = 0;
        $scope.loadInterceptors();
    };

    $scope.clearFilters = function () {
        $scope.selectedProfile = 'ALL';
        $scope.selectedProfileEnvironments = null;
        $scope.searchText = '';
        $scope.selectedSort = 'name_asc';
        $scope.liveOnly = false;
        $scope.paging.page = 0;
        $scope.loadInterceptors();
    };

    // ─── Pagination ─────────────────────────────────────
    $scope.goPrevPage = function () {
        if ($scope.paging.page > 0) {
            $scope.paging.page--;
            $scope.loadInterceptors();
        }
    };

    $scope.goNextPage = function () {
        if ($scope.paging.page + 1 < $scope.paging.totalPages) {
            $scope.paging.page++;
            $scope.loadInterceptors();
        }
    };

    // ─── Dialog ─────────────────────────────────────────
    $scope.close = function () {
        if (refreshTimer) { $interval.cancel(refreshTimer); }
        if (searchDebounceTimer) { $timeout.cancel(searchDebounceTimer); }
        $mdDialog.hide();
    };

    // ================================================================
    // ENVIRONMENT DOTS — identical rules/labels to BizInterceptorsTabController
    // (env.display_status is server-computed, same field either screen reads).
    // ================================================================

    var ENV_DOT_CLASS_BY_STATUS = {
        ONLINE: 'ipd-env-dot--on',
        STANDBY: 'ipd-env-dot--warn',
        STALLED: 'ipd-env-dot--stalled',
        AGENT_UNREACHABLE: 'ipd-env-dot--agent-unreachable',
        OFFLINE: 'ipd-env-dot--off'
    };

    $scope.envDotClass = function (env) {
        return ENV_DOT_CLASS_BY_STATUS[env.display_status] || 'ipd-env-dot--off';
    };

    function envMetricAlertActive(metricConfig, environmentId) {
        if (!metricConfig || !metricConfig.environment_statuses || !environmentId) { return false; }
        return metricConfig.environment_statuses.some(function (s) {
            return s.environment_id === environmentId && s.status === 'SET_CONDITION';
        });
    }

    $scope.envHasAlert = function (ic, env) {
        if (typeof env.alert_active === 'boolean') { return env.alert_active; }
        var mc = (ic && ic.monitoring_config) || {};
        return envMetricAlertActive(mc.pressure, env.id) || envMetricAlertActive(mc.agent_latency, env.id);
    };

    $scope.envDotTooltip = function (ic, env) {
        var label;
        switch (env.display_status) {
            case 'ONLINE':
                label = env.env_name + ': Live';
                break;
            case 'STANDBY':
                label = env.env_name + ': Online, not live';
                break;
            case 'STALLED':
                label = env.env_name + ': Stalled — agent not online, uploads paused';
                break;
            case 'AGENT_UNREACHABLE':
                var connectionError = env.agent_connection_error || (env.stats && env.stats.agent_connection && env.stats.agent_connection.agent_connection_error);
                label = env.env_name + ': Disconnected from agent' + (connectionError ? ' — ' + connectionError : '');
                break;
            default:
                label = env.env_name + ': Offline';
        }
        return $scope.envHasAlert(ic, env) ? (label + ' — ⚠ SLA alert active') : label;
    };

    $scope.avgLoadPressureLabel = function (ic) {
        if (!ic || ic.avg_load_pressure === null || ic.avg_load_pressure === undefined) { return '—'; }
        return Math.round(ic.avg_load_pressure) + '%';
    };

    $scope.avgPingTimeLabel = function (ic) {
        if (!ic || ic.avg_ping_time_ms === null || ic.avg_ping_time_ms === undefined) { return '—'; }
        return Math.round(ic.avg_ping_time_ms) + 'ms';
    };

    $scope.openEnvironmentDialog = function ($event, interceptor, env) {
        if ($event) { $event.stopPropagation(); }
        $scope._refreshPaused = true;
        var detailPromise = BizInterceptorService.getById(interceptor.biz_interceptor_id)
            .then(function (response) { return response.data; });
        $mdDialog.show({
            controller: 'EnvironmentStatusDialogController',
            controllerAs: 'vm',
            templateUrl: 'profile/tabs/biz-interceptors/environment-status-dialog/environment-status-dialog.html',
            parent: angular.element(document.body),
            clickOutsideToClose: true,
            escapeToClose: true,
            multiple: true,
            resolve: {
                env: function () {
                    return detailPromise.then(function (detail) {
                        return (detail.environments || []).find(function (item) { return item.id === env.id; }) || env;
                    });
                },
                monitoringConfig: function () {
                    return detailPromise.then(function (detail) { return detail.monitoring_config; });
                }
            },
            locals: {
                bizInterceptorId: interceptor.biz_interceptor_id,
                bizInterceptorName: interceptor.biz_interceptor_name
            }
        }).catch(angular.noop).finally(function () { $scope._refreshPaused = false; });
    };

    // ================================================================
    // ROW ACTIONS — same dialogs BizInterceptorsTabController opens,
    // same locals shape; every one of these only needs the interceptor
    // row itself except Edit (needs the full Profile — fetched below).
    // ================================================================

    $scope.openConsolidatedStatsDialog = function ($event, interceptor) {
        if ($event) { $event.stopPropagation(); }
        $scope._refreshPaused = true;
        $mdDialog.show({
            controller: 'ConsolidatedStatsDialogController',
            controllerAs: 'vm',
            templateUrl: 'profile/tabs/biz-interceptors/consolidated-stats-dialog/consolidated-stats-dialog.html',
            parent: angular.element(document.body),
            clickOutsideToClose: true,
            escapeToClose: true,
            multiple: true,
            resolve: {
                interceptor: function () {
                    return BizInterceptorService.getById(interceptor.biz_interceptor_id).then(function (response) { return response.data; });
                }
            }
        }).catch(angular.noop).finally(function () { $scope._refreshPaused = false; });
    };

    $scope.openMetricsExplorerDialog = function ($event, interceptor) {
        if ($event) { $event.stopPropagation(); }
        $scope._refreshPaused = true;
        var releaseRefresh = BizInterceptorService.pauseListRefresh();
        return $mdDialog.show({
            controller: 'MetricsExplorerDialogController',
            templateUrl: 'profile/tabs/biz-interceptors/metrics-explorer-dialog/metrics-explorer-dialog.html',
            parent: angular.element(document.body),
            clickOutsideToClose: true,
            escapeToClose: true,
            multiple: true,
            resolve: {
                interceptor: function () {
                    return BizInterceptorService.getById(interceptor.biz_interceptor_id).then(function (response) { return response.data; });
                }
            },
            locals: { environment: null }
        }).catch(angular.noop).finally(function () {
            releaseRefresh();
            $scope._refreshPaused = false;
            if (!listDestroyed) $scope.loadInterceptors(true);
        });
    };

    $scope.openDownloadArtifactDialog = function ($event, interceptor) {
        if ($event) { $event.stopPropagation(); }
        $mdDialog.show({
            controller: 'InterceptorDownloadDialogController',
            controllerAs: 'vm',
            templateUrl: 'profile/tabs/biz-interceptors/interceptor-download-dialog/interceptor-download-dialog.html',
            parent: angular.element(document.body),
            clickOutsideToClose: true,
            escapeToClose: true,
            multiple: true,
            locals: { interceptor: interceptor, bundle: null }
        }).catch(angular.noop);
    };

    // Edit needs the full Profile (environments + name) that the list row
    // doesn't carry — fetched on demand right before the wizard opens.
    // `loadingEdit` gates just that row's edit icon so a slow fetch can't
    // be clicked twice.
    $scope.loadingEdit = {};

    $scope.openEditBizInterceptorDialog = function ($event, interceptor) {
        if ($event) { $event.stopPropagation(); }
        var id = interceptor.biz_interceptor_id;
        if ($scope.loadingEdit[id]) { return; }
        $scope.loadingEdit[id] = true;

        ProfileService.getProfileById(interceptor.profile_id)
            .then(function (response) {
                var profile = response.data;
                $scope._refreshPaused = true;
                return $mdDialog.show({
                    controller: 'EditBizInterceptorDialogController',
                    controllerAs: 'vm',
                    templateUrl: 'profile/tabs/biz-interceptors/edit-wizard/edit-biz-interceptor-dialog.html',
                    parent: angular.element(document.body),
                    clickOutsideToClose: false,
                    escapeToClose: false,
                    multiple: true,
                    locals: { interceptor: interceptor, profile: profile }
                });
            })
            .then(function (result) {
                if (result && result.updated) {
                    $mdToast.show(
                        $mdToast.simple()
                            .textContent('Biz interceptor updated successfully')
                            .position('top right')
                            .hideDelay(3000)
                    );
                    $scope.loadInterceptors();
                }
            })
            .catch(function (err) {
                if (err) {
                    console.error('Error loading profile for edit:', err);
                    dialogService.showErrorDialog('Load Error', 'Could not load this interceptor\'s profile.');
                }
            })
            .finally(function () {
                $scope.loadingEdit[id] = false;
                $scope._refreshPaused = false;
            });
    };

    $scope.onToggleBizInterceptorEnabled = function (interceptor) {
        BizInterceptorService.patch(interceptor.biz_interceptor_id, { enabled: interceptor.enabled })
            .then(function () {
                $mdToast.show(
                    $mdToast.simple()
                        .textContent('Biz interceptor ' + (interceptor.enabled ? 'enabled' : 'disabled'))
                        .position('top right')
                        .hideDelay(2000)
                );
                $scope.loadInterceptors(true);
            })
            .catch(function (error) {
                if (error && error.status === -1) return;
                interceptor.enabled = !interceptor.enabled; // revert on error
                console.error('Error toggling enabled:', error);
                dialogService.showErrorDialog(
                    'Update Failed',
                    (error.data && error.data.message) || 'Could not update biz interceptor.'
                );
            });
    };

    // ─── Delete — double-click-to-confirm icon + confirmation dialog,
    // same pattern as BizInterceptorsTabController. ─────
    $scope.confirmDeleteMap = {};
    var deleteTimers = {};
    var CONFIRM_TIMEOUT_MS = 3000;

    $scope.deleteBizInterceptor = function ($event, interceptor) {
        if ($event) { $event.stopPropagation(); }
        var id = interceptor.biz_interceptor_id;

        if (!$scope.confirmDeleteMap[id]) {
            $scope.confirmDeleteMap[id] = true;
            if (deleteTimers[id]) { $timeout.cancel(deleteTimers[id]); }
            deleteTimers[id] = $timeout(function () {
                $scope.confirmDeleteMap[id] = false;
                delete deleteTimers[id];
            }, CONFIRM_TIMEOUT_MS);
            return;
        }

        $timeout.cancel(deleteTimers[id]);
        delete deleteTimers[id];
        $scope.confirmDeleteMap[id] = false;

        dialogService.showConfirmationDialog(
            'Delete Biz Interceptor',
            'You are about to permanently delete <strong>' + interceptor.biz_interceptor_name +
                '</strong>. This action cannot be undone.',
            interceptor.biz_interceptor_name,
            true
        ).then(function (decision) {
            if (decision !== 'confirmed') { return; }

            BizInterceptorService.delete(id)
                .then(function () {
                    $mdToast.show(
                        $mdToast.simple()
                            .textContent('Biz interceptor deleted successfully')
                            .position('top right')
                            .hideDelay(3000)
                    );
                    $scope.loadInterceptors();
                })
                .catch(function (err) {
                    console.error('Delete error:', err);
                    dialogService.showErrorDialog(
                        'Delete Failed',
                        (err.data && err.data.message) || 'Could not delete biz interceptor.'
                    );
                });
        });
    };

    // ─── Auto-refresh — paused while any row dialog is open, same
    // reasoning as BizInterceptorsTabController's own _listRefreshPaused. ──
    $scope._refreshPaused = false;

    refreshTimer = $interval(function () {
        if ($scope._refreshPaused) { return; }
        $scope.loadInterceptors(true);
    }, refreshIntervalMs);

    $scope.$on('$destroy', function () {
        listDestroyed = true;
        if (refreshTimer) { $interval.cancel(refreshTimer); }
        if (searchDebounceTimer) { $timeout.cancel(searchDebounceTimer); }
        Object.keys(deleteTimers).forEach(function (k) { $timeout.cancel(deleteTimers[k]); });
    });

    // ─── Init ───────────────────────────────────────────
    _loadFilterData();
    $scope.loadInterceptors();
});
